import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { useAppSelector, useAppDispatch } from '../store';
import { setUser, setBank, setPso } from '../store/slices';
import { findBankAndPsoByAuthUidWithFallback, findBankByAuthUid } from '../screens/login/service/auth';
import Login from '../screens/login/view/Login';
import DashboardLayout from '../layout/DashboardLayout';
import InchargeDashboard from '../screens/dashboard/InchargeDashboard';
import MisInchargeDashboard from '../screens/dashboard/MisInchargeDashboard';
import RegionalHeadDashboard from '../screens/dashboard/RegionalHeadDashboard';
import HeadDashboard from '../screens/dashboard/HeadDashboard';
import PsoDashboard from '../screens/dashboard/PsoDashboard';
import Branches from '../screens/branches/view/Branches';
import Locations from '../screens/branches/view/Locations';
import Companies from '../screens/companies/view/Companies';
import PSO from '../screens/pso/view/PSO';
import Managers from '../screens/managers/view/Managers';
import Protocols from '../screens/protocol/view/Protocol';
import Surveys from '../screens/surveys_management/survey/view/Surveys';
import NewLocationSurveys from '../screens/surveys_management/new_location_survey/view/NewLocationSurveys';
import LeaseRenewalSurveys from '../screens/surveys_management/lease_renewal_survey/view/LeaseRenewalSurveys';
import RequestForm from '../screens/request_management/request_form/view/RequestForm';
import RequestSummary from '../screens/request_summary/view/RequestSummary';
import Inventory from '../screens/manage_items/items/view/Inventory';
import ManageCCTVHardware from '../screens/manage_hardware/cctv/view/CCTV';
import ManageBAMSHardware from '../screens/manage_hardware/bams/view/BAMS';
import CCTV from '../screens/hardware_request/cctv/view/CCTV';
import BAMS from '../screens/hardware_request/bams/view/BAMS';
import PurchaseOrderCCTV from '../screens/hardware_request/purchase_order_cctv/view/PurchaseOrderCCTV';
import PurchaseOrderBAMS from '../screens/hardware_request/purchase_order_bams/view/PurchaseOrderBAMS';
import Deployments from '../screens/deployments/view/Deployments';
import ChangeOfVendors from '../screens/request_management/change_of_vendor/view/ChangeOfVendors';
import TravellingApproval from '../screens/travelling_approval/view/TravellingApproval';
import Loader from '../components/Loader';
import { getPostLoginHomePathForPso } from '../navigation/postLoginHomePath';
import { designationIsIncharge, designationIsMisIncharge } from '../utils/designationRole';

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const user = useAppSelector((s) => s.user.currentUser);
    const bank = useAppSelector((s) => s.user.bank);
    const pso = useAppSelector((s) => s.user.pso);
    const location = useLocation();

    if (!user || !bank) {
        // Save the current path so we can redirect back after login
        const returnPath = location.pathname + location.search;
        sessionStorage.setItem('returnPath', returnPath);
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    const isProtocolOfficer = pso?.designation?.toLowerCase().replace(/_/g, ' ') === 'protocol officer';
    if (isProtocolOfficer && location.pathname !== '/protocols') {
        return <Navigate to="/protocols" replace />;
    }

    const isInchargeHR = pso?.designation?.toLowerCase().replace(/_/g, ' ') === 'incharge hr';
    if (isInchargeHR && location.pathname !== '/travelling-approval') {
        return <Navigate to="/travelling-approval" replace />;
    }

    return <>{children}</>;
}

// Public Route Component (redirects to home if already logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
    const user = useAppSelector((s) => s.user.currentUser);
    const bank = useAppSelector((s) => s.user.bank);
    const pso = useAppSelector((s) => s.user.pso);

    if (user && bank) {
        const home = pso ? getPostLoginHomePathForPso(pso) : '/dashboard';
        return <Navigate to={home} replace />;
    }

    return <>{children}</>;
}

/** Unknown paths: send guests to app root (→ /login); show 404 only when logged in. */
function NotFoundOrHome() {
    const user = useAppSelector((s) => s.user.currentUser);
    const bank = useAppSelector((s) => s.user.bank);

    if (!user || !bank) {
        return <Navigate to="/" replace />;
    }

    return (
        <div className="flex min-h-screen items-center justify-center">
            <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100">404</h1>
                <p className="mt-2 text-gray-600 dark:text-gray-400">Page not found</p>
            </div>
        </div>
    );
}

// Dashboard Content Component - shows different dashboards based on user type
function DashboardContent() {
    const pso = useAppSelector((s) => s.user.pso);
    const designation = pso?.designation || '';
    const isIncharge = designationIsIncharge(designation);
    const isMisIncharge = designationIsMisIncharge(designation);
    const isRegional = /regional/i.test(designation);
    const isHead = /(^|\s)head(\s|$)/i.test((designation || '').replace(/_/g, ' ')) && !isRegional;

    if (isHead) {
        return <HeadDashboard />;
    }

    if (isRegional) {
        return <RegionalHeadDashboard />;
    }

    if (isMisIncharge) {
        return <MisInchargeDashboard />;
    }

    if (isIncharge) {
        return <InchargeDashboard />;
    }

    if (pso) {
        return <PsoDashboard />;
    }

    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Dashboard</h2>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Welcome to your dashboard</p>
        </div>
    );
}

function AppRoutes() {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = React.useState(true);
    const [authChecked, setAuthChecked] = React.useState(false);
    const user = useAppSelector((s) => s.user.currentUser);
    const bank = useAppSelector((s) => s.user.bank);
    const pso = useAppSelector((s) => s.user.pso);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            // Check if we are impersonating a user
            const impersonatedUserStr = sessionStorage.getItem('impersonatedUser');
            if (impersonatedUserStr) {
                try {
                    const pso = JSON.parse(impersonatedUserStr);
                    const userRecord = {
                        uid: pso.authUid || pso.id,
                        email: pso.email || '',
                        displayName: pso.name || '',
                        photoURL: null,
                    };
                    dispatch(setUser(userRecord));

                    const savedBank = sessionStorage.getItem('impersonatedBank');
                    if (savedBank) {
                        dispatch(setBank(JSON.parse(savedBank)));
                    } else if (firebaseUser) {
                        const bankResult = await findBankByAuthUid(firebaseUser.uid);
                        if (bankResult) {
                            dispatch(setBank(bankResult.bank));
                        }
                    }

                    dispatch(setPso({
                        authUid: pso.authUid || '',
                        name: pso.name || '',
                        email: pso.email || '',
                        contact: pso.contact || '',
                        designation: pso.designation || '',
                        createdAt: pso.createdAt || '',
                    }));

                    const isIncharge = designationIsIncharge(pso.designation);
                    sessionStorage.setItem('role', isIncharge ? 'incharge' : 'pso');
                    if (pso.designation) {
                        sessionStorage.setItem('psoDesignation', pso.designation);
                    } else {
                        sessionStorage.removeItem('psoDesignation');
                    }
                    sessionStorage.setItem('psoDocId', pso.id);

                    setAuthChecked(true);
                    setTimeout(() => setLoading(false), 300);
                    return;
                } catch (e) {
                    console.error('Error restoring impersonation state:', e);
                }
            }

            // Check if we're in the middle of creating a user (admin creating staff)
            const creatingUserFlag = sessionStorage.getItem('creatingUser');
            
            if (creatingUserFlag) {
                if (firebaseUser && firebaseUser.uid === creatingUserFlag) {
                    console.log('Admin session restored. Clearing user creation flag.');
                    sessionStorage.removeItem('creatingUser');
                } else {
                    console.log('Ignoring auth state change during user creation. Current user:', firebaseUser?.uid, 'Admin:', creatingUserFlag);
                    return;
                }
            }
            
            if (firebaseUser) {
                // User is signed in, restore user data
                const userRecord = {
                    uid: firebaseUser.uid,
                    email: firebaseUser.email,
                    displayName: firebaseUser.displayName ?? null,
                    photoURL: firebaseUser.photoURL ?? null,
                };
                dispatch(setUser(userRecord));

                try {
                    // Try to find PSO first
                    const { bankId, psoId: psoDocId, bank, pso } = await findBankAndPsoByAuthUidWithFallback(firebaseUser.uid);
                    dispatch(setBank({
                        userId: bank.userId,
                        companyId: bank.companyId,
                        bankDocId: bankId,
                        companyName: bank.companyName,
                        companyNo: bank.companyNo,
                        companyAddress: bank.companyAddress,
                        email: bank.email,
                        imgUrl: bank.imgUrl,
                        createdAt: bank.createdAt,
                    }));
                    dispatch(setPso({
                        authUid: pso.authUid,
                        name: pso.name,
                        email: pso.email,
                        contact: pso.contact,
                        designation: pso.designation,
                        createdAt: pso.createdAt,
                    }));
                    // Store the Firestore doc IDs so subcollection lookups work correctly
                    sessionStorage.setItem('bankDocId', bankId);
                    sessionStorage.setItem('psoDocId', psoDocId);
                    // Set role based on designation - incharge users are still stored as 'pso' role
                    // but the designation field identifies them as incharge
                    const isIncharge = designationIsIncharge(pso.designation);
                    sessionStorage.setItem('role', isIncharge ? 'incharge' : 'pso');
                    if (pso.designation) sessionStorage.setItem('psoDesignation', pso.designation);
                    else sessionStorage.removeItem('psoDesignation');
                } catch (e) {
                    // If PSO not found, try bank
                    const bankResult = await findBankByAuthUid(firebaseUser.uid);
                    if (bankResult) {
                        const { bank } = bankResult;
                        dispatch(setBank({
                            userId: bank.userId,
                            companyId: bank.companyId,
                            companyName: bank.companyName,
                            companyNo: bank.companyNo,
                            companyAddress: bank.companyAddress,
                            email: bank.email,
                            imgUrl: bank.imgUrl,
                            createdAt: bank.createdAt,
                        }));
                        dispatch(setPso(null));
                        sessionStorage.setItem('role', 'bank');
                        sessionStorage.removeItem('psoDesignation');
                    } else {
                        // No bank or PSO found, clear user
                        dispatch(setUser(null));
                        dispatch(setBank(null));
                        dispatch(setPso(null));
                    }
                }
            } else {
                // User is signed out, clear state
                // Also clear the creatingUser flag if it exists
                sessionStorage.removeItem('creatingUser');
                sessionStorage.removeItem('impersonatedUser');
                sessionStorage.removeItem('impersonatedBank');
                dispatch(setUser(null));
                dispatch(setBank(null));
                dispatch(setPso(null));
                sessionStorage.removeItem('role');
                sessionStorage.removeItem('psoDesignation');
                sessionStorage.removeItem('returnPath');
            }
            setAuthChecked(true);
            // Add a small delay for smoother UX
            setTimeout(() => setLoading(false), 300);
        });

        return () => unsubscribe();
    }, [dispatch]);

    // Keep loading until auth is checked
    if (!authChecked || loading) {
        return <Loader />;
    }

    return (
        <div className="min-h-screen relative">
            <Routes>
                {/* Public Routes */}
                <Route
                    path="/login"
                    element={
                        <PublicRoute>
                            <Login />
                        </PublicRoute>
                    }
                />

                {/* Protected Routes */}
                <Route
                    path="/dashboard"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <DashboardContent />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/branches"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <Branches />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/locations"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <Locations />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/companies"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <Companies />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/pso"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <PSO />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/managers"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <Managers />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/protocols"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <Protocols />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/surveys"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <Surveys />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/new-location-surveys"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <NewLocationSurveys />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/lease-renewal-surveys"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <LeaseRenewalSurveys />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/request-form"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <RequestForm />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/request-summary"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <RequestSummary />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/change-of-vendor"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <ChangeOfVendors />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/travelling-approval"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <TravellingApproval />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/inventory"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <Inventory />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/manage-hardware-cctv"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <ManageCCTVHardware />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/manage-hardware-bams"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <ManageBAMSHardware />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/cctv"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <CCTV />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/bams"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <BAMS />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/po-cctv"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <PurchaseOrderCCTV />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/po-bams"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <PurchaseOrderBAMS />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/deployments"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <Deployments />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/change-of-vendor"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout>
                                <ChangeOfVendors />
                            </DashboardLayout>
                        </ProtectedRoute>
                    }
                />

                {/* Default Route */}
                <Route
                    path="/"
                    element={
                        user && bank ? (
                            <Navigate to={pso ? getPostLoginHomePathForPso(pso) : '/dashboard'} replace />
                        ) : (
                            <Navigate to="/login" replace />
                        )
                    }
                />

                {/* Unknown paths → / (guests then see /login via "/" route) */}
                <Route path="*" element={<NotFoundOrHome />} />
            </Routes>
        </div>
    );
}

export default function AppRouter() {
    return (
        <BrowserRouter>
            <AppRoutes />
        </BrowserRouter>
    );
}

